"use client";

/**
 * ChatbotSelector Component
 *
 * Dropdown selector for choosing which chatbot configuration to use.
 * Shows in the message composer area.
 */
import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect } from "react";
import type { Chatbot } from "@/types/chatbot";
type ChatbotSelectorProps = {
  /** List of available chatbots */
  chatbots: Chatbot[];
  /** Currently selected chatbot (null = using defaults) */
  selectedChatbot: Chatbot | null;
  /** Called when a chatbot is selected */
  onSelectChatbot: (chatbot: Chatbot | null) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Compact mode (smaller size) */
  compact?: boolean;
  /** Additional class names */
  className?: string;
};
export function ChatbotSelector(t0) {
  const $ = _c(35);
  const {
    chatbots,
    selectedChatbot,
    onSelectChatbot,
    disabled: t1,
    loading: t2,
    compact: t3,
    className: t4
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const loading = t2 === undefined ? false : t2;
  const compact = t3 === undefined ? false : t3;
  const className = t4 === undefined ? "" : t4;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  let t5;
  let t6;
  if ($[0] !== isOpen) {
    t5 = () => {
      const handleClickOutside = event => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearchTerm("");
        }
      };
      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }
    };
    t6 = [isOpen];
    $[0] = isOpen;
    $[1] = t5;
    $[2] = t6;
  } else {
    t5 = $[1];
    t6 = $[2];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[3] !== isOpen) {
    t7 = () => {
      if (isOpen && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };
    t8 = [isOpen];
    $[3] = isOpen;
    $[4] = t7;
    $[5] = t8;
  } else {
    t7 = $[4];
    t8 = $[5];
  }
  useEffect(t7, t8);
  let t9;
  if ($[6] !== chatbots || $[7] !== className || $[8] !== compact || $[9] !== disabled || $[10] !== isOpen || $[11] !== loading || $[12] !== onSelectChatbot || $[13] !== searchTerm || $[14] !== selectedChatbot) {
    let t10;
    if ($[16] !== searchTerm) {
      t10 = chatbot => chatbot.name.toLowerCase().includes(searchTerm.toLowerCase()) || chatbot.description && chatbot.description.toLowerCase().includes(searchTerm.toLowerCase());
      $[16] = searchTerm;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    const filteredChatbots = chatbots.filter(t10);
    let t11;
    if ($[18] !== onSelectChatbot) {
      t11 = chatbot_0 => {
        onSelectChatbot(chatbot_0);
        setIsOpen(false);
        setSearchTerm("");
      };
      $[18] = onSelectChatbot;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    const handleSelect = t11;
    const buttonClasses = compact ? "h-8 px-2 text-xs" : "h-9 px-3 text-sm";
    let t12;
    if ($[20] !== disabled || $[21] !== isOpen) {
      t12 = () => !disabled && setIsOpen(!isOpen);
      $[20] = disabled;
      $[21] = isOpen;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    const t13 = disabled || loading;
    const t14 = `flex items-center gap-2 rounded-md border border-foreground/30 bg-white/60 text-foreground hover:bg-white/80 focus:outline-none focus:ring-2 focus:ring-sky/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${buttonClasses}`;
    let t15;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <svg className="h-4 w-4 flex-shrink-0 text-foreground/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    const t16 = loading ? "Loading..." : selectedChatbot ? selectedChatbot.name : "Default";
    let t17;
    if ($[24] !== t16) {
      t17 = <span className="truncate max-w-[120px]">{t16}</span>;
      $[24] = t16;
      $[25] = t17;
    } else {
      t17 = $[25];
    }
    const t18 = `h-4 w-4 flex-shrink-0 text-foreground/60 transition-transform ${isOpen ? "rotate-180" : ""}`;
    let t19;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />;
      $[26] = t19;
    } else {
      t19 = $[26];
    }
    let t20;
    if ($[27] !== t18) {
      t20 = <svg className={t18} fill="none" stroke="currentColor" viewBox="0 0 24 24">{t19}</svg>;
      $[27] = t18;
      $[28] = t20;
    } else {
      t20 = $[28];
    }
    let t21;
    if ($[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== t17 || $[33] !== t20) {
      t21 = <button type="button" onClick={t12} disabled={t13} className={t14}>{t15}{t17}{t20}</button>;
      $[29] = t12;
      $[30] = t13;
      $[31] = t14;
      $[32] = t17;
      $[33] = t20;
      $[34] = t21;
    } else {
      t21 = $[34];
    }
    t9 = <div className={`relative ${className}`} ref={dropdownRef}>{t21}{isOpen && <div className="absolute left-0 bottom-full mb-1 w-64 rounded-md border border-foreground/20 bg-white/90 backdrop-blur-md shadow-lg z-50">{chatbots.length > 5 && <div className="p-2 border-b border-foreground/10"><input ref={searchInputRef} type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search chatbots..." className="w-full rounded-md border border-foreground/20 bg-white/60 px-2 py-1.5 text-sm text-foreground placeholder-foreground/40 focus:border-sky/50 focus:outline-none focus:ring-1 focus:ring-sky/50" /></div>}<div className="max-h-64 overflow-y-auto py-1"><button type="button" onClick={() => handleSelect(null)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-white/60 transition-colors ${!selectedChatbot ? "bg-white/60 text-foreground" : "text-foreground"}`}><div className="h-6 w-6 flex items-center justify-center rounded-full bg-foreground/10 text-xs font-medium text-foreground/60">D</div><div className="flex-1 min-w-0"><div className="font-medium truncate">Default Settings</div><div className="text-xs text-foreground/50 truncate">Use global defaults</div></div>{!selectedChatbot && <svg className="h-4 w-4 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</button>{filteredChatbots.length > 0 && <div className="my-1 border-t border-foreground/10" />}{filteredChatbots.length === 0 && searchTerm && <div className="px-3 py-2 text-sm text-foreground/50">No chatbots found</div>}{filteredChatbots.length === 0 && !searchTerm && chatbots.length === 0 && <div className="px-3 py-2 text-sm text-foreground/50">No chatbots yet. Create one in the sidebar.</div>}{filteredChatbots.map(chatbot_1 => {
            const isSelected = selectedChatbot?.id === chatbot_1.id;
            const initial = chatbot_1.name.charAt(0).toUpperCase();
            const provider = chatbot_1.config.model?.provider || "ai";
            const providerColor = {
              openai: "bg-green-500",
              claude: "bg-orange-500",
              grok: "bg-blue-500",
              gemini: "bg-purple-500"
            }[provider] || "bg-foreground/30";
            return <button key={chatbot_1.id} type="button" onClick={() => handleSelect(chatbot_1)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-white/60 transition-colors ${isSelected ? "bg-white/60 text-foreground" : "text-foreground"}`}><div className={`h-6 w-6 flex items-center justify-center rounded-full ${providerColor} text-xs font-medium text-white`}>{initial}</div><div className="flex-1 min-w-0"><div className="font-medium truncate">{chatbot_1.name}</div>{chatbot_1.description && <div className="text-xs text-foreground/50 truncate">{chatbot_1.description}</div>}</div>{isSelected && <svg className="h-4 w-4 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</button>;
          })}</div></div>}</div>;
    $[6] = chatbots;
    $[7] = className;
    $[8] = compact;
    $[9] = disabled;
    $[10] = isOpen;
    $[11] = loading;
    $[12] = onSelectChatbot;
    $[13] = searchTerm;
    $[14] = selectedChatbot;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}